// src/components/admin/quotes/AdminQuoteList.jsx
import { useState, useMemo } from 'react';
import {
  FaEye,
  FaTrash,
  FaFileAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaHome,
  FaCog,
  FaSpinner,
  FaClock
} from 'react-icons/fa';
import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { useSiteConfig } from '../../../hooks/useSiteConfig';

const AdminQuoteList = ({ quotes, loading, onView, onDelete }) => {
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const { dateFormat, timeFormat } = useSiteConfig();

  const normalizeDate = (date) => {
    if (!date) return null;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    if (date instanceof Date && isValid(date)) return date;
    if (typeof date === 'string') {
      const parsed = parseISO(date);
      if (isValid(parsed)) return parsed;
    }
    if (typeof date === 'number') {
      const parsed = new Date(date);
      if (isValid(parsed)) return parsed;
    }
    return null;
  };

  const getDateFormatPattern = () => {
    switch (dateFormat) {
      case 'MM/DD/YYYY': return 'MM/dd/yyyy';
      case 'DD/MM/YYYY': return 'dd/MM/yyyy';
      case 'YYYY-MM-DD': return 'yyyy-MM-dd';
      case 'MMM DD, YYYY': return 'MMM dd, yyyy';
      default: return 'MM/dd/yyyy';
    }
  };

  const getTimeFormatPattern = () => timeFormat === '24h' ? 'HH:mm' : 'h:mm a';

  const formatDateOnly = (date) => {
    const normalized = normalizeDate(date);
    if (!normalized) return 'N/A';
    try {
      return format(normalized, getDateFormatPattern());
    } catch {
      return 'Invalid date';
    }
  };

  const formatTimeOnly = (date) => {
    const normalized = normalizeDate(date);
    if (!normalized) return '';
    try {
      return format(normalized, getTimeFormatPattern());
    } catch {
      return '';
    }
  };

  const formatRelativeTime = (date) => {
    const normalized = normalizeDate(date);
    if (!normalized) return '';
    try {
      return formatDistanceToNow(normalized, { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getCustomerName = (quote) => {
    if (quote.customerName) return quote.customerName;
    if (quote.firstName || quote.lastName) {
      return `${quote.firstName || ''} ${quote.lastName || ''}`.trim() || 'Unknown Customer';
    }
    return quote.name || 'Unknown Customer';
  };

  const getServiceName = (quote) => {
    if (quote.service?.name) return quote.service.name;
    if (quote.serviceRequested) {
      const serviceMap = {
        'general-inquiry': 'General Inquiry',
        'landscaping': 'Landscaping',
        'lawn-care': 'Lawn Care',
        'hardscaping': 'Hardscaping',
        'maintenance': 'Maintenance',
        'design': 'Design Services'
      };
      return serviceMap[quote.serviceRequested] || quote.serviceRequested;
    }
    return 'General Inquiry';
  };

  const getCategoryIcon = (quote) => {
    const service = quote.service?.name || quote.serviceRequested || 'general';
    if (service.toLowerCase().includes('lawn')) return <FaCog className="w-4 h-4" />;
    if (service.toLowerCase().includes('landscape')) return <FaHome className="w-4 h-4" />;
    if (service.toLowerCase().includes('hardscape')) return <FaHome className="w-4 h-4" />;
    if (service.toLowerCase().includes('maintenance')) return <FaCog className="w-4 h-4" />;
    return <FaFileAlt className="w-4 h-4" />;
  };

  const getAddress = (quote) => quote.propertyAddress || quote.address || '—';

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <FaSort className="w-3 h-3" />;
    return sortOrder === 'asc' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  const filteredQuotes = useMemo(() => {
    let filtered = quotes.filter(quote => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const customerName = getCustomerName(quote).toLowerCase();
        const serviceName = getServiceName(quote).toLowerCase();
        const email = (quote.customerEmail || quote.email || '').toLowerCase();
        const address = getAddress(quote).toLowerCase();
        if (
          !customerName.includes(term) &&
          !serviceName.includes(term) &&
          !email.includes(term) &&
          !address.includes(term)
        ) {
          return false;
        }
      }

      if (sourceFilter !== 'all') {
        if (sourceFilter === 'quote_requests' && quote.sourceCollection !== 'quote_requests') return false;
        if (sourceFilter === 'quotes' && quote.sourceCollection !== 'quotes') return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'customerName':
          aValue = getCustomerName(a).toLowerCase();
          bValue = getCustomerName(b).toLowerCase();
          break;
        case 'serviceName':
          aValue = getServiceName(a).toLowerCase();
          bValue = getServiceName(b).toLowerCase();
          break;
        case 'address':
          aValue = getAddress(a).toLowerCase();
          bValue = getAddress(b).toLowerCase();
          break;
        case 'createdAt':
        default:
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
      }
      if (sortOrder === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [quotes, searchTerm, sourceFilter, sortBy, sortOrder]);

  const sourceOptions = [...new Set(quotes.map(q => q.sourceCollection).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading quotes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, service, email, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Sources</option>
            {sourceOptions.map(source => (
              <option key={source} value={source}>
                {source === 'quote_requests' ? 'Guest Requests' : 'User Quotes'}
              </option>
            ))}
          </select>

          <button
            onClick={() => { setSearchTerm(''); setSourceFilter('all'); }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-600 space-x-2">
          <FaFileAlt className="w-4 h-4" />
          <span>Showing {filteredQuotes.length} of {quotes.length} quotes</span>
        </div>
        <div className="flex items-center text-sm text-gray-500 space-x-1">
          <FaClock className="w-4 h-4" />
          <span>Local Time</span>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('customerName')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Customer</span>
                    {getSortIcon('customerName')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('serviceName')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Service</span>
                    {getSortIcon('serviceName')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('address')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Property Address</span>
                    {getSortIcon('address')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('createdAt')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Requested</span>
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuotes.map((quote) => (
                <tr key={quote.id} className="hover:bg-gray-50">
                  {/* Customer */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {getCustomerName(quote)}
                      </div>
                      <div className="text-xs text-gray-500 flex flex-col gap-0.5 mt-0.5">
                        {(quote.customerEmail || quote.email) && (
                          <span className="flex items-center gap-1">
                            <FaEnvelope className="w-3 h-3" />
                            {quote.customerEmail || quote.email}
                          </span>
                        )}
                        {(quote.customerPhone || quote.phone) && (
                          <span className="flex items-center gap-1">
                            <FaPhone className="w-3 h-3" />
                            {quote.customerPhone || quote.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Service */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="text-gray-500">{getCategoryIcon(quote)}</div>
                      <div className="text-sm font-medium text-gray-900">
                        {getServiceName(quote)}
                      </div>
                    </div>
                  </td>

                  {/* Address */}
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-1.5 text-sm text-gray-700 max-w-xs">
                      <FaMapMarkerAlt className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="break-words">{getAddress(quote)}</span>
                    </div>
                  </td>

                  {/* Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDateOnly(quote.createdAt || quote.requestedAt)}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <span>{formatTimeOnly(quote.createdAt || quote.requestedAt)}</span>
                      <span>•</span>
                      <span>{formatRelativeTime(quote.createdAt || quote.requestedAt)}</span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onView(quote)}
                        className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded transition-colors"
                        title="View Details"
                      >
                        <FaEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(quote.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                        title="Delete Quote"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredQuotes.length === 0 && (
          <div className="text-center py-12">
            <FaFileAlt className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No quotes found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || sourceFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'No quote requests have been submitted yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuoteList;